/**
 * /admin/sports — token-gated single-page console for live game ops.
 *
 * iter-85 — built to let the owner fire moments + paste fresh quote
 * lines from a phone during a live game. Auth is the
 * `SPORTS_ADMIN_TOKEN` env value sent via the `X-Admin-Token` header.
 * The token is captured once on first visit (saved to localStorage,
 * never to the URL) and used for every subsequent call.
 *
 * Two tabs:
 *   1. MOMENT — one-tap buttons that POST /api/admin/sports/event with
 *      a preset moment_type + headline. Pulls active games to show a
 *      game-switcher pill.
 *   2. QUOTES — paste a JSON array conforming to sports_quotes.json's
 *      `quotes` shape and POST it to /api/admin/sports/quotes/bulk.
 *      Shows the seed file's documentation inline so the user always
 *      has the format reference.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { Toaster } from "../components/ui/sonner";
import { Zap, Target, Send, Sparkles, RotateCcw, AlertCircle, LogOut, Image as ImageIcon, Upload, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "moodcard.admin.sports_token";

// One-tap moment buttons matching the quote bank moment_type vocabulary.
// Each entry maps to /api/admin/sports/event payloads — headline / side
// / player are filled in by sub-controls before the POST fires.
const MOMENT_PRESETS = [
    { key: "denial",     label: "Block",      icon: Target,      tone: "petty" },
    { key: "statement",  label: "Dunk",       icon: Zap,         tone: "proud" },
    { key: "bullseye",   label: "Three",      icon: Target,      tone: "proud" },
    { key: "robbery",    label: "Steal",      icon: Sparkles,    tone: "funny" },
    { key: "dominance",  label: "Run 8-0",    icon: Zap,         tone: "petty" },
    { key: "momentum",   label: "Lead change", icon: RotateCcw,  tone: "funny" },
    { key: "clutch",     label: "Clutch shot", icon: Target,     tone: "proud" },
    { key: "crossover",  label: "Crossover",   icon: Sparkles,   tone: "funny" },
    { key: "ref_drama",  label: "Bad call",    icon: AlertCircle, tone: "petty" },
    { key: "victory",    label: "Win",         icon: Zap,         tone: "proud" },
    { key: "pain",       label: "Loss",        icon: AlertCircle, tone: "pain" },
];

function TokenGate({ onUnlock }) {
    const [token, setToken] = useState("");
    return (
        <div className="min-h-screen bg-app text-app flex items-center justify-center px-6">
            <div className="w-full max-w-md">
                <div className="text-[10px] tracking-[0.32em] uppercase text-app-3 mb-2">restricted</div>
                <h1 className="font-display text-2xl text-app mb-4">live ops console</h1>
                <p className="font-serif italic text-app-2 text-sm mb-6">
                    paste your sports admin token. it's stored only on this device.
                </p>
                <input
                    type="password"
                    autoComplete="off"
                    placeholder="mc_..."
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                    data-testid="admin-token-input"
                    className="w-full bg-black/40 border border-white/12 rounded-xl px-4 py-3 text-app font-mono focus:border-amber-300/55 focus:outline-none"
                />
                <button
                    type="button"
                    onClick={() => {
                        const t = token.trim();
                        if (!t) { toast.error("paste a token"); return; }
                        try { window.localStorage.setItem(TOKEN_KEY, t); } catch { /* */ }
                        onUnlock(t);
                    }}
                    data-testid="admin-token-submit"
                    className="mt-3 w-full inline-flex items-center justify-center gap-2 py-3 rounded-full bg-amber-400 hover:bg-amber-300 text-black font-medium text-sm tracking-wide transition-colors"
                >
                    unlock
                </button>
            </div>
        </div>
    );
}

function MomentTab({ token, games }) {
    const [selectedGame, setSelectedGame] = useState(games[0]?.game_slug || null);
    const [side, setSide] = useState("");
    const [player, setPlayer] = useState("");
    const [customHeadline, setCustomHeadline] = useState("");
    const [recent, setRecent] = useState([]);
    const [simRunning, setSimRunning] = useState(false);
    const [simInterval, setSimInterval] = useState(20);

    const game = games.find((g) => g.game_slug === selectedGame);
    const teamA = game?.away_team;
    const teamB = game?.home_team;

    const refreshSimStatus = async () => {
        try {
            const { data } = await axios.get(`${API}/admin/sports/simulate/status`, { headers: { "X-Admin-Token": token } });
            setSimRunning((data?.running || []).includes(selectedGame));
        } catch { /* */ }
    };

    useEffect(() => { if (selectedGame) refreshSimStatus(); /* eslint-disable-line */ }, [selectedGame]);

    const startSim = async () => {
        try {
            await axios.post(
                `${API}/admin/sports/simulate/start`,
                { game_slug: selectedGame, interval: simInterval },
                { headers: { "X-Admin-Token": token } },
            );
            setSimRunning(true);
            toast.success(`simulation running · 1 moment every ${simInterval}s`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "could not start sim");
        }
    };

    const stopSim = async () => {
        try {
            await axios.post(
                `${API}/admin/sports/simulate/stop`,
                { game_slug: selectedGame },
                { headers: { "X-Admin-Token": token } },
            );
            setSimRunning(false);
            toast.success("simulation stopped");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "could not stop sim");
        }
    };

    const flipStatus = async (newStatus) => {
        try {
            await axios.post(
                `${API}/admin/sports/games/${selectedGame}/status`,
                { status: newStatus },
                { headers: { "X-Admin-Token": token } },
            );
            toast.success(`status → ${newStatus}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "status flip failed");
        }
    };

    const fire = async (preset) => {
        if (!selectedGame) { toast.error("pick a game first"); return; }
        const headline = customHeadline.trim()
            || (player ? `${player} ${preset.label.toLowerCase()}` : `${preset.label}`);
        try {
            const r = await axios.post(
                `${API}/admin/sports/event`,
                {
                    game_slug: selectedGame,
                    moment_type: preset.key,
                    headline,
                    side: side || null,
                    player: player || null,
                },
                { headers: { "X-Admin-Token": token } },
            );
            toast.success(`${preset.label} fired · viewers will see it in ~15s`);
            setRecent((arr) => [{ ...r.data?.event, at: new Date() }, ...arr.slice(0, 9)]);
            setCustomHeadline("");
        } catch (err) {
            const msg = err?.response?.data?.detail || "fire failed";
            toast.error(msg);
        }
    };

    if (!games.length) {
        return (
            <p className="font-serif italic text-app-2 text-center py-12">
                no games registered yet. create one via POST /api/admin/sports/games or seed sports_quotes.json.
            </p>
        );
    }

    return (
        <>
            {/* Game picker */}
            <div className="text-[10px] tracking-[0.32em] uppercase text-app-3 mb-2">game</div>
            <div className="flex flex-wrap gap-2 mb-5">
                {games.map((g) => (
                    <button
                        key={g.game_slug}
                        type="button"
                        onClick={() => setSelectedGame(g.game_slug)}
                        data-testid={`admin-game-${g.game_slug}`}
                        className={`px-3 py-1.5 rounded-full text-[11px] tracking-[0.18em] uppercase border ${
                            selectedGame === g.game_slug
                                ? "bg-amber-400 text-black border-amber-300"
                                : "bg-black/30 text-app-2 border-white/12"
                        }`}
                    >
                        {g.label || g.game_slug}
                    </button>
                ))}
            </div>

            {/* Side + player + custom headline */}
            <div className="grid grid-cols-2 gap-2 mb-3">
                <button
                    type="button"
                    onClick={() => setSide("")}
                    className={`px-3 py-2 rounded-xl text-[11px] tracking-[0.18em] uppercase border ${
                        side === "" ? "bg-white/10 border-white/30 text-app" : "bg-black/25 border-white/12 text-app-2"
                    }`}
                >
                    no side
                </button>
                <div className="grid grid-cols-2 gap-2">
                    {[teamA, teamB].filter(Boolean).map((t) => (
                        <button
                            key={t.id}
                            type="button"
                            onClick={() => setSide(t.id)}
                            className={`px-2 py-2 rounded-xl text-[11px] tracking-[0.18em] uppercase border ${
                                side === t.id ? "bg-amber-400/15 border-amber-300/45 text-app" : "bg-black/25 border-white/12 text-app-2"
                            }`}
                        >
                            {t.short}
                        </button>
                    ))}
                </div>
            </div>

            <input
                type="text"
                value={player}
                onChange={(e) => setPlayer(e.target.value)}
                placeholder="player (e.g. wembanyama) — optional"
                data-testid="admin-player-input"
                className="w-full bg-black/30 border border-white/12 rounded-xl px-3 py-2 mb-2 font-mono text-sm placeholder-app-3/60 focus:border-amber-300/55 focus:outline-none"
            />
            <input
                type="text"
                value={customHeadline}
                onChange={(e) => setCustomHeadline(e.target.value)}
                placeholder="headline override — optional"
                data-testid="admin-headline-input"
                className="w-full bg-black/30 border border-white/12 rounded-xl px-3 py-2 mb-5 text-sm placeholder-app-3/60 focus:border-amber-300/55 focus:outline-none"
            />

            {/* iter-88 — Simulator + status controls */}
            <div className="mb-5 p-3 rounded-2xl border border-amber-300/20 bg-amber-400/5">
                <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] tracking-[0.32em] uppercase text-amber-200">
                        simulator
                    </div>
                    <div className="flex items-center gap-1.5">
                        <button
                            type="button"
                            onClick={() => flipStatus("live")}
                            data-testid="admin-status-live"
                            className="px-2 py-0.5 rounded-full text-[9px] tracking-wider uppercase border border-rose-400/40 bg-rose-500/15 text-rose-200 hover:bg-rose-500/25 transition-colors"
                        >
                            go live
                        </button>
                        <button
                            type="button"
                            onClick={() => flipStatus("scheduled")}
                            data-testid="admin-status-scheduled"
                            className="px-2 py-0.5 rounded-full text-[9px] tracking-wider uppercase border border-white/15 bg-black/30 text-app-2 hover:text-app transition-colors"
                        >
                            reset
                        </button>
                    </div>
                </div>
                <p className="font-serif italic text-[11px] text-app-3 mb-3">
                    fires random moments every N seconds. flips the game to live automatically.
                </p>
                <div className="flex items-center gap-2 mb-2">
                    <label className="text-[10px] uppercase tracking-wider text-app-3">interval</label>
                    <input
                        type="range"
                        min="8"
                        max="60"
                        step="2"
                        value={simInterval}
                        onChange={(e) => setSimInterval(parseInt(e.target.value, 10))}
                        disabled={simRunning}
                        data-testid="admin-sim-interval"
                        className="flex-1"
                    />
                    <span className="font-mono text-xs text-app w-12 text-right">{simInterval}s</span>
                </div>
                {simRunning ? (
                    <button
                        type="button"
                        onClick={stopSim}
                        data-testid="admin-sim-stop"
                        className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-full bg-rose-500 hover:bg-rose-400 text-white font-medium text-sm tracking-wide transition-colors"
                    >
                        ⏹ stop simulation
                    </button>
                ) : (
                    <button
                        type="button"
                        onClick={startSim}
                        data-testid="admin-sim-start"
                        className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-full bg-amber-400 hover:bg-amber-300 text-black font-medium text-sm tracking-wide transition-colors"
                    >
                        🎬 start simulation
                    </button>
                )}
            </div>

            {/* One-tap moment buttons */}
            <div className="text-[10px] tracking-[0.32em] uppercase text-app-3 mb-2">fire a moment</div>
            <div className="grid grid-cols-3 gap-2 mb-7">
                {MOMENT_PRESETS.map((preset) => {
                    const Icon = preset.icon;
                    return (
                        <button
                            key={preset.key}
                            type="button"
                            onClick={() => fire(preset)}
                            data-testid={`admin-moment-${preset.key}`}
                            className="px-3 py-4 rounded-2xl border border-white/12 bg-black/30 hover:border-amber-300/45 hover:bg-amber-400/8 active:scale-[0.97] transition-all flex flex-col items-center gap-1.5"
                        >
                            <Icon className="w-4 h-4 text-amber-200" strokeWidth={1.8} />
                            <span className="font-sans text-[11px] uppercase tracking-wide text-app">
                                {preset.label}
                            </span>
                            <span className="font-mono text-[9px] text-app-3">
                                {preset.key}
                            </span>
                        </button>
                    );
                })}
            </div>

            {/* Recent fires */}
            {recent.length > 0 && (
                <>
                    <div className="text-[10px] tracking-[0.32em] uppercase text-app-3 mb-2">last fired</div>
                    <div className="space-y-2">
                        {recent.map((e, i) => (
                            <div
                                key={(e?.created_at || "") + i}
                                className="px-3 py-2 rounded-xl border border-white/8 bg-black/30 font-mono text-xs text-app-2 flex items-center justify-between"
                            >
                                <span className="truncate">
                                    {e.moment_type} · {e.headline || "—"}
                                </span>
                                <Send className="w-3 h-3 text-emerald-300 shrink-0" strokeWidth={2} />
                            </div>
                        ))}
                    </div>
                </>
            )}
        </>
    );
}

function QuotesTab({ token }) {
    const [json, setJson] = useState(SAMPLE_JSON);
    const [busy, setBusy] = useState(false);
    const [last, setLast] = useState(null);

    const submit = async () => {
        let parsed;
        try {
            parsed = JSON.parse(json);
        } catch (e) {
            toast.error("invalid JSON: " + (e.message || "parse error"));
            return;
        }
        // Accept either a top-level array or { quotes: [...] }
        const quotes = Array.isArray(parsed) ? parsed : parsed.quotes;
        if (!Array.isArray(quotes) || !quotes.length) {
            toast.error("need an array of quotes (or { quotes: [...] })");
            return;
        }
        setBusy(true);
        try {
            const { data } = await axios.post(
                `${API}/admin/sports/quotes/bulk`,
                { quotes },
                { headers: { "X-Admin-Token": token } },
            );
            setLast(data);
            toast.success(`${data.inserted} added · ${data.skipped} skipped (already present)`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "bulk import failed");
        } finally { setBusy(false); }
    };

    return (
        <>
            <div className="text-[10px] tracking-[0.32em] uppercase text-app-3 mb-2">paste quote JSON</div>
            <p className="font-serif italic text-app-3 text-[11px] mb-3 leading-relaxed">
                shape: array of <code className="font-mono text-app-2">{`{ game_slug, side, moment_type, tone, copy }`}</code>.
                duplicates (same 5-tuple) skip silently. tone ∈ petty | proud | funny | pain | respectful.
            </p>
            <textarea
                rows={14}
                spellCheck={false}
                value={json}
                onChange={(e) => setJson(e.target.value)}
                data-testid="admin-quotes-json"
                className="w-full bg-black/40 border border-white/12 rounded-xl px-3 py-3 font-mono text-[12px] leading-relaxed placeholder-app-3/60 focus:border-amber-300/55 focus:outline-none resize-none"
            />
            <button
                type="button"
                disabled={busy}
                onClick={submit}
                data-testid="admin-quotes-submit"
                className="mt-3 w-full inline-flex items-center justify-center gap-2 py-3 rounded-full bg-amber-400 hover:bg-amber-300 disabled:opacity-60 text-black font-medium text-sm tracking-wide transition-colors"
            >
                {busy ? "uploading..." : "import quotes"}
            </button>

            {last && (
                <div className="mt-3 px-3 py-2 rounded-xl border border-emerald-400/30 bg-emerald-400/5 font-mono text-xs text-emerald-200">
                    inserted: {last.inserted} · skipped: {last.skipped}
                </div>
            )}

            <details className="mt-6">
                <summary className="text-[10px] tracking-[0.32em] uppercase text-app-3 cursor-pointer hover:text-app">
                    quote format reference
                </summary>
                <pre className="mt-2 px-3 py-3 rounded-xl bg-black/40 border border-white/8 font-mono text-[10px] text-app-2 leading-relaxed overflow-x-auto whitespace-pre">
{FORMAT_DOCS}
                </pre>
            </details>
        </>
    );
}

const SAMPLE_JSON = `[
  {
    "game_slug": "spurs-vs-knicks",
    "side": "spurs",
    "moment_type": "denial",
    "tone": "funny",
    "copy": "we don't serve that here.",
    "player": "wembanyama"
  }
]`;

const FORMAT_DOCS = `Required fields:
  game_slug    — "spurs-vs-knicks"
  side         — "spurs" | "knicks" | "neutral"
  moment_type  — denial | statement | bullseye | robbery |
                 dominance | momentum | victory | pain |
                 ref_drama | clutch | crossover | pregame |
                 always
  tone         — petty | proud | funny | pain | respectful
  copy         — the card line (keep < 12 words)

Optional fields:
  title_hashtag — "DO_NOT_TEST_IT" (overrides default # title)
  player        — "wembanyama" (short id)
  pack          — force a specific style pack
  tags          — array of free-form tags
  max_words     — hard word cap`;

function BackgroundsTab({ token, games }) {
    const [stats, setStats] = useState(null);
    const [busy, setBusy] = useState(false);
    const [lastResult, setLastResult] = useState(null);
    const [showManifest, setShowManifest] = useState(false);

    const reload = async () => {
        try {
            const { data } = await axios.get(`${API}/admin/sports/backgrounds`, {
                headers: { "X-Admin-Token": token },
            });
            setStats(data);
        } catch (err) {
            toast.error("failed to load backgrounds: " + (err?.response?.data?.detail || err.message));
        }
    };

    useEffect(() => {
        reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onZipPick = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.name.toLowerCase().endsWith(".zip")) {
            toast.error("must be a .zip file");
            return;
        }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await axios.post(
                `${API}/admin/sports/backgrounds/import-zip`,
                fd,
                { headers: { "X-Admin-Token": token, "Content-Type": "multipart/form-data" } },
            );
            setLastResult(data);
            toast.success(`${data.inserted} added · ${data.failed_count} failed`);
            await reload();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "zip import failed");
        } finally {
            setBusy(false);
            e.target.value = ""; // allow re-picking the same file
        }
    };

    const deleteOne = async (id) => {
        if (!window.confirm("delete this background?")) return;
        try {
            await axios.delete(`${API}/admin/sports/backgrounds/${id}`, {
                headers: { "X-Admin-Token": token },
            });
            toast.success("deleted");
            await reload();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "delete failed");
        }
    };

    return (
        <>
            <div className="text-[10px] tracking-[0.32em] uppercase text-app-3 mb-2">background library</div>
            <p className="font-serif italic text-app-3 text-[11px] mb-3 leading-relaxed">
                upload a zip with <code className="font-mono text-app-2">manifest.json</code> at the root.
                images are auto-optimized to 3 sizes (1080×1920 · 1080×1080 · 600×600) and stored as webp.
            </p>

            {/* Stats */}
            {stats && (
                <div className="grid grid-cols-3 gap-2 mb-4">
                    <div className="px-3 py-3 rounded-xl border border-white/10 bg-black/25 text-center">
                        <div className="font-display text-2xl text-app">{stats.total ?? 0}</div>
                        <div className="text-[9px] tracking-[0.22em] uppercase text-app-3">total</div>
                    </div>
                    <div className="px-3 py-3 rounded-xl border border-white/10 bg-black/25 text-center">
                        <div className="font-display text-2xl text-app">{Object.keys(stats.by_team || {}).length}</div>
                        <div className="text-[9px] tracking-[0.22em] uppercase text-app-3">teams</div>
                    </div>
                    <div className="px-3 py-3 rounded-xl border border-white/10 bg-black/25 text-center">
                        <div className="font-display text-2xl text-app">{Object.keys(stats.by_kind || {}).length}</div>
                        <div className="text-[9px] tracking-[0.22em] uppercase text-app-3">kinds</div>
                    </div>
                </div>
            )}

            {/* Per-team breakdown */}
            {stats?.by_team && Object.keys(stats.by_team).length > 0 && (
                <div className="mb-4 flex flex-wrap gap-2">
                    {Object.entries(stats.by_team).map(([team, n]) => (
                        <span key={team} className="px-2.5 py-1 rounded-full bg-black/30 border border-white/10 text-[10px] tracking-wide text-app-2">
                            {team}: <span className="text-app">{n}</span>
                        </span>
                    ))}
                </div>
            )}

            {/* ZIP uploader */}
            <label
                className={`flex flex-col items-center justify-center gap-2 py-8 rounded-2xl border-2 border-dashed transition-colors cursor-pointer ${
                    busy ? "border-amber-300/50 bg-amber-400/5" : "border-white/15 hover:border-amber-300/40 bg-black/25 hover:bg-amber-400/5"
                }`}
                data-testid="admin-bg-uploader"
            >
                <input
                    type="file"
                    accept=".zip"
                    onChange={onZipPick}
                    disabled={busy}
                    className="hidden"
                    data-testid="admin-bg-zip-input"
                />
                <Upload className="w-6 h-6 text-amber-200" strokeWidth={1.6} />
                <div className="text-center">
                    <div className="font-sans text-sm text-app">
                        {busy ? "uploading + optimizing..." : "drop a .zip with manifest.json"}
                    </div>
                    <div className="font-serif italic text-[11px] text-app-3 mt-0.5">
                        backgrounds auto-resize to 3 sizes (full · square · thumb)
                    </div>
                </div>
            </label>

            {lastResult && (
                <div className="mt-3 px-3 py-2 rounded-xl border border-emerald-400/30 bg-emerald-400/5 font-mono text-xs text-emerald-200">
                    inserted: {lastResult.inserted} · failed: {lastResult.failed_count} · library total: {lastResult.total_in_library}
                </div>
            )}
            {lastResult?.failures && lastResult.failures.length > 0 && (
                <details className="mt-2">
                    <summary className="text-[10px] tracking-[0.22em] uppercase text-rose-300 cursor-pointer">
                        {lastResult.failures.length} failures · expand
                    </summary>
                    <pre className="mt-2 px-3 py-3 rounded-xl bg-black/40 border border-rose-400/20 font-mono text-[10px] text-rose-200 leading-relaxed overflow-x-auto whitespace-pre-wrap">
                        {JSON.stringify(lastResult.failures, null, 2)}
                    </pre>
                </details>
            )}

            {/* Manifest format */}
            <details className="mt-6" onToggle={(e) => setShowManifest(e.target.open)}>
                <summary className="text-[10px] tracking-[0.32em] uppercase text-app-3 cursor-pointer hover:text-app">
                    manifest.json format
                </summary>
                <pre className="mt-2 px-3 py-3 rounded-xl bg-black/40 border border-white/8 font-mono text-[10px] text-app-2 leading-relaxed overflow-x-auto whitespace-pre">
{BG_MANIFEST_DOCS}
                </pre>
            </details>

            {/* Recent items grid */}
            {stats?.items && stats.items.length > 0 && (
                <>
                    <div className="mt-6 text-[10px] tracking-[0.32em] uppercase text-app-3 mb-2">
                        latest ({stats.items.length})
                    </div>
                    <div className="grid grid-cols-3 gap-2" data-testid="admin-bg-grid">
                        {stats.items.slice(0, 18).map((bg) => (
                            <div
                                key={bg.id}
                                className="relative aspect-square rounded-xl overflow-hidden border border-white/10 group"
                                data-testid={`admin-bg-tile-${bg.id}`}
                            >
                                <img
                                    src={`${process.env.REACT_APP_BACKEND_URL}${bg.url_thumb}`}
                                    alt=""
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                />
                                <div className="absolute inset-x-0 bottom-0 px-1.5 py-1 bg-black/70 backdrop-blur-sm flex items-center justify-between">
                                    <span className="font-mono text-[8px] tracking-wider uppercase text-white/90 truncate flex-1">
                                        {bg.team_id} · {bg.moment_type}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => deleteOne(bg.id)}
                                        className="ml-1 text-rose-300 hover:text-rose-200 opacity-0 group-hover:opacity-100 transition-opacity"
                                        title="delete"
                                    >
                                        <Trash2 className="w-3 h-3" strokeWidth={1.8} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </>
    );
}

const BG_MANIFEST_DOCS = `{
  "_version": "1.0",
  "backgrounds": [
    {
      "file": "spurs/stadium-night.jpg",  // path inside zip
      "team_id": "spurs",                 // or "knicks" / "neutral"
      "kind": "team_identity",            // team_identity | player | moment_art
      "moment_type": "any",               // matches all moments; or denial/statement/...
      "tone": "any",                      // matches all tones; or proud/petty/funny/...
      "tags": ["silver_black"],           // optional, free-form
      "weight": 1.0                       // sampling weight; defaults to 1.0
    },
    {
      "file": "spurs/wemby-silhouette.png",
      "team_id": "spurs",
      "kind": "player",
      "moment_type": "denial",
      "tone": "petty",
      "tags": ["wemby"]
    },
    {
      "file": "moments/block-denied.jpg",
      "team_id": "neutral",
      "kind": "moment_art",
      "moment_type": "denial",
      "tone": "any"
    }
  ]
}`;

export default function AdminSports() {
    const [token, setToken] = useState(null);
    const [tab, setTab] = useState("moment");
    const [games, setGames] = useState([]);

    useEffect(() => {
        try {
            const stored = window.localStorage.getItem(TOKEN_KEY);
            if (stored) setToken(stored);
        } catch { /* */ }
    }, []);

    useEffect(() => {
        if (!token) return;
        // Use public list endpoint (no auth needed); just so the moment tab
        // knows which games exist.
        axios.get(`${API}/sports/games`)
            .then(({ data }) => setGames(data?.games || []))
            .catch(() => setGames([]));
    }, [token]);

    if (!token) {
        return (
            <>
                <Toaster theme="dark" position="bottom-center" />
                <TokenGate onUnlock={setToken} />
            </>
        );
    }

    const logout = () => {
        try { window.localStorage.removeItem(TOKEN_KEY); } catch { /* */ }
        setToken(null);
    };

    return (
        <div className="min-h-screen bg-app text-app pb-24">
            <Helmet>
                <title>Sports Ops · MoodCard</title>
                <meta name="robots" content="noindex,nofollow" />
            </Helmet>
            <Toaster theme="dark" position="bottom-center" />

            <header className="sticky top-0 z-30 px-5 py-4 bg-app/90 backdrop-blur-md border-b border-white/6 flex items-center justify-between">
                <div>
                    <div className="text-[9px] tracking-[0.32em] uppercase text-app-3">live ops</div>
                    <div className="font-display text-base tracking-[0.18em] uppercase text-app">
                        sports console
                    </div>
                </div>
                <button
                    type="button"
                    onClick={logout}
                    data-testid="admin-logout"
                    className="inline-flex items-center gap-1.5 text-app-3 hover:text-app text-[11px] tracking-wide"
                >
                    <LogOut className="w-3 h-3" strokeWidth={1.6} />
                    forget token
                </button>
            </header>

            <div className="max-w-md mx-auto px-5 pt-5">
                <div className="flex gap-2 mb-5" role="tablist">
                    <button
                        type="button"
                        role="tab"
                        aria-selected={tab === "moment"}
                        onClick={() => setTab("moment")}
                        data-testid="admin-tab-moment"
                        className={`flex-1 py-2.5 rounded-full text-[11px] tracking-[0.18em] uppercase border ${
                            tab === "moment" ? "bg-amber-400 text-black border-amber-300" : "bg-black/25 text-app-2 border-white/12"
                        }`}
                    >
                        moment
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={tab === "quotes"}
                        onClick={() => setTab("quotes")}
                        data-testid="admin-tab-quotes"
                        className={`flex-1 py-2.5 rounded-full text-[11px] tracking-[0.18em] uppercase border ${
                            tab === "quotes" ? "bg-amber-400 text-black border-amber-300" : "bg-black/25 text-app-2 border-white/12"
                        }`}
                    >
                        quotes
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={tab === "backgrounds"}
                        onClick={() => setTab("backgrounds")}
                        data-testid="admin-tab-backgrounds"
                        className={`flex-1 py-2.5 rounded-full text-[11px] tracking-[0.18em] uppercase border ${
                            tab === "backgrounds" ? "bg-amber-400 text-black border-amber-300" : "bg-black/25 text-app-2 border-white/12"
                        }`}
                    >
                        backgrounds
                    </button>
                </div>

                {tab === "moment" && <MomentTab token={token} games={games} />}
                {tab === "quotes" && <QuotesTab token={token} />}
                {tab === "backgrounds" && <BackgroundsTab token={token} games={games} />}
            </div>
        </div>
    );
}
